import React, { useState, useEffect, forwardRef } from "react";
import api from "../api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../css/EnterpriseDetails.css";
import { formatDateToUserTimeZone } from "../utils/formatDate";
import { formatDateToUser } from "../utils/formatDate";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import editIcon from "../assets/Images/Edit.png";
import viewIcon from "../assets/Images/View.png";
import changeStatusIcon from "../assets/Images/ChangeStatus.png";
import crossIcon from "../assets/icons/crossIcon.png";
import addIcon from "../assets/Images/AddIcon.png";
import downloadIcon from "../assets/Images/Download.png";
import filterIcon from "../assets/Images/Filter.png";

import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../utils/formatDate";
import subscriptionIcon from "../assets/icons/SubscriptionIcon.png";
import { useTableSort } from "../hooks/useTableSort";
import { SortableHeader } from "../components/SortableHeader";

const SUBSCRIPTION_API_URL = `${process.env.REACT_APP_API_URL}/subscriptions`;

const SubscriptionManagement = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    subscription_name: "",
    subscription_description: "",
    amount: "0",
    plan_type: "Select Plan Type",
    start_date: "",
    end_date: "",
    subscription_status: "1",
  });
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [dayFilter, setDayFilter] = useState("");
  const [viewMode, setViewMode] = useState(false);
  const [subscriptionStats, setSubscriptionStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    expired: 0,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const handleReset = () => {
    setSortOrder("asc");
    setDayFilter("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleApply = () => {
    // console.log("Sorting by:", sortOrder);
    // console.log("Filtering by day:", dayFilter);
  };

  const fetchSubscriptions = async () => {
    if (!token) {
      showErrorToast(TOAST_MESSAGES.UNAUTHORIZED);
      return;
    }
    try {
      const response = await api.get(SUBSCRIPTION_API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptions(response.data);
    } catch (error) {
      console.error("Failed to fetch subscriptions", error);
      showErrorToast(TOAST_MESSAGES.SUBSCRIPTION.FETCH_ERROR);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const filteredSubscriptions = subscriptions
    .filter((subscription) => {
      const lower = searchTerm.toLowerCase();
      const subscriptionName = subscription.subscription_name
        ? subscription.subscription_name.toLowerCase()
        : "";
      const subscriptionDesc = subscription.subscription_description
        ? subscription.subscription_description.toLowerCase()
        : "";
      return (
        subscriptionName.includes(lower) || subscriptionDesc.includes(lower)
      );
    })
    .filter((subscription) => {
      if (!dayFilter) return true;
      const subscriptionDate = new Date(subscription.created_timestamp);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayFilter === "Today") return subscriptionDate >= today;
      if (dayFilter === "Yesterday") {
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        return subscriptionDate >= yesterday && subscriptionDate < today;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.updated_timestamp);
      const dateB = new Date(b.updated_timestamp);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

  const totalEntries = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filteredSubscriptions.slice(indexOfFirst, indexOfLast);
  const showingFrom = totalEntries === 0 ? 0 : indexOfFirst + 1;
  const showingTo = Math.min(indexOfLast, totalEntries);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const { sortedData, requestSort, sortConfig } = useTableSort(currentData);

  const handleEntriesChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageClick = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscription?"))
      return;
    try {
      await api.delete(`${SUBSCRIPTION_API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchSubscriptions();
      if (currentData.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (subscription) => {
    setFormData({
      subscription_name: subscription.subscription_name,
      subscription_description: subscription.subscription_description || "",
      amount: subscription.subscription_amount ?? "",
      plan_type: subscription.subscription_plan_type || "Basic",
      start_date: subscription.subscription_start_date?.slice(0, 10) || "",
      end_date: subscription.subscription_end_date?.slice(0, 10) || "",
      subscription_status: subscription.subscription_status || "1",
    });

    setEditingSubscriptionId(subscription.subscription_id);
    setViewMode(false);
    setShowForm(true);
  };

  const handleView = (subscription) => {
    setFormData({
      subscription_name: subscription.subscription_name,
      subscription_description: subscription.subscription_description || "",
      amount: subscription.subscription_amount ?? "",
      plan_type: subscription.subscription_plan_type || "Basic",
      start_date: subscription.subscription_start_date?.slice(0, 10) || "",
      end_date: subscription.subscription_end_date?.slice(0, 10) || "",
    });

    setEditingSubscriptionId(subscription.subscription_id);
    setViewMode(true);
    setShowForm(true);
  };

  const handleChangeStatus = async (subscription) => {
    const current = subscription.subscription_status;
    let newStatus;

    if (current === "1") newStatus = "0";
    else if (current === "0") newStatus = "1";
    else {
      showErrorToast("Cannot change status of Expired subscription");
      return;
    }

    const statusText = newStatus === "1" ? "Active" : "Inactive";

    try {
      await api.put(
        `${SUBSCRIPTION_API_URL}/change-status/${subscription.subscription_id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      fetchSubscriptions();
      fetchStats();

      showSuccessToast(
        `${TOAST_MESSAGES.SUBSCRIPTION.STATUS_CHANGE_SUCCESS} to ${statusText}`,
      );
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.SUBSCRIPTION.STATUS_CHANGE_ERROR);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get(`${SUBSCRIPTION_API_URL}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubscriptionStats(res.data);
    } catch (err) {
      console.error("Failed to fetch subscription stats:", err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (showForm && !editingSubscriptionId) {
      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({ ...prev, start_date: today }));
    }
  }, [showForm, editingSubscriptionId]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (new Date(formData.end_date) <= new Date(formData.start_date)) {
      showErrorToast("End date must be after start date.");
      return;
    }

    setIsSubmitting(true); // 👈 Start loading

    const payload = {
      subscription_name: formData.subscription_name,
      subscription_description: formData.subscription_description,
      subscription_amount: parseFloat(formData.amount),
      subscription_plan_type: formData.plan_type,
      subscription_start_date: formData.start_date,
      subscription_end_date: formData.end_date,
      subscription_status: formData.subscription_status || "1",
    };

    try {
      const url = editingSubscriptionId
        ? `${SUBSCRIPTION_API_URL}/${editingSubscriptionId}`
        : SUBSCRIPTION_API_URL;
      const method = editingSubscriptionId ? "PUT" : "POST";

      await api({
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      });

      setFormData({
        subscription_name: "",
        subscription_description: "",
        amount: "",
        plan_type: "",
        start_date: "",
        end_date: "",
      });
      setShowForm(false);
      setEditingSubscriptionId(null);
      fetchSubscriptions();
      showSuccessToast(
        editingSubscriptionId
          ? TOAST_MESSAGES.SUBSCRIPTION.UPDATE_SUCCESS
          : TOAST_MESSAGES.SUBSCRIPTION.CREATE_SUCCESS,
      );
    } catch (err) {
      console.error("Error creating/updating subscription:", err);
      console.error("Error creating/updating subscription:", err);

      const backendMessage =
        err?.response?.data?.message ||
        (editingSubscriptionId
          ? TOAST_MESSAGES.SUBSCRIPTION.UPDATE_ERROR
          : TOAST_MESSAGES.SUBSCRIPTION.CREATE_ERROR);

      showErrorToast(backendMessage);
    } finally {
      setIsSubmitting(false); // 👈 Stop loading
    }
  };

  const closeModal = () => {
    setShowForm(false);
    setFormData({
      subscription_name: "",
      subscription_description: "",
      amount: "0",
      plan_type: "",
      start_date: "",
      end_date: "",
    });
    setEditingSubscriptionId(null);
    setViewMode(false);
  };

  const exportExcel = () => {
    const wsData = [
      ["Subscription", "Description", "Status"],
      ...filteredSubscriptions.map((p) => [
        p.subscription_name,
        p.subscription_description || "",
        // p.subscription_status === "1" ? "Active" : "Inactive",
        p.subscription_status === "1"
          ? "Active"
          : p.subscription_status === "0"
            ? "Inactive"
            : "Expired",
      ]),
    ];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "Subscriptions");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "subscription_management.xlsx");
  };

  return (
    <div className="mx-2 pt-1  h-100">
      <div className="d-flex flex-wrap gap-3 mb-0 align-items-stretch">
        {/* Total Subscription */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Total"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Total Subscriptions</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.total}</h2>
        </div>

        {/* Active Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Active"
              className="icon-image1"
              style={{
                width: "22px",
                height: "22px",
              }}
            />
            <h6 className="ms-2 mb-0">Active Subscriptions</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.active}</h2>
        </div>

        {/* Inactive Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Inactive"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Inactive Subscriptions</h6>
          </div>
          <h2 className="fw-bold">{subscriptionStats.inactive}</h2>
        </div>

        {/* Expired Subscriptions */}
        <div className="dashboard-box p-3 rounded shadow-sm d-flex flex-column justify-content-between">
          <div className="d-flex align-items-center mb-2">
            <img
              src={subscriptionIcon}
              alt="Expired"
              style={{
                width: "22px",
                height: "22px",
              }}
              className="icon-image1"
            />
            <h6 className="ms-2 mb-0">Expired Subscriptions</h6>
          </div>
          <h2 className="fw-bold ">{subscriptionStats.expired}</h2>
        </div>
      </div>

      <div className="d-flex refresh-prompts justify-content-between align-items-center mb-2 mt-1 ">
        <span className="fw-bold" style={{ fontSize: "20px" }}>
          Subscription Management Details
        </span>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center">
          <select
            className="form-select w-auto"
            value={entriesPerPage}
            onChange={handleEntriesChange}
          >
            {[50, 100, 500].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="ms-2 mt-2">Entries per page</label>
        </div>

        <div className="d-flex align-items-center">
          <div className="custom-search-wrapper me-3">
            <div className="input-group custom-search-group">
              <span className="input-group-text">
                <i className="bi bi-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search Subscriptions"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <button
            className="btn custom btn-outline-secondary me-2 action-button"
            onClick={exportExcel}
            title="Export to Excel"
          >
            <img src={downloadIcon} className="icon-image" alt="Download" />
          </button>

          <div className="">
            <button
              className="icon-button filter-dropdown-toggle action-button"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              title="Filter"
            >
              <img src={filterIcon} className="icon-image" alt="Filter" />
            </button>

            <div
              className="dropdown-menu profile-menu-dropdown p-2"
              style={{ minWidth: "200px" }}
            >
              <h6 className="fw-bolder refresh-prompts px-0">Time</h6>
              <hr className="mt-0" />
              {["asc", "desc"].map((order) => (
                <div className="form-check" key={order}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sortOrder"
                    id={order}
                    value={order}
                    checked={sortOrder === order}
                    onChange={() => setSortOrder(order)}
                  />
                  <label
                    className="form-check-label notification-label"
                    htmlFor={order}
                  >
                    {order === "asc" ? "Ascending" : "Descending"}
                  </label>
                </div>
              ))}

              <hr className="my-2" />
              <h6 className="fw-bolder refresh-prompts px-0">By Day</h6>
              <hr className="mt-0" />
              {["Today", "Yesterday", ""].map((day) => (
                <div className="form-check" key={day || "all"}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="dayFilter"
                    id={day || "all"}
                    value={day}
                    checked={dayFilter === day}
                    onChange={() => setDayFilter(day)}
                  />
                  <label
                    className="form-check-label fw-5 notification-label"
                    htmlFor={day || "all"}
                  >
                    {day || "All"}
                  </label>
                </div>
              ))}

              <hr className="mt-1" />
              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleReset}
                >
                  Reset
                </button>
                <button
                  className="btn dropdown-btn bg-dark text-white btn-sm"
                  onClick={handleApply}
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div>

          <button
            className="btn btn-outline-secondary action-button text-dark"
            onClick={() => setShowForm(true)}
            title="Add Subscription"
          >
            <img src={addIcon} className="icon-image" alt="Add" />
          </button>
        </div>
      </div>

      {totalEntries === 0 ? (
        <div className="text-center my-5">
          <p className="refresh-prompts ak-text-md">No entries found.</p>
        </div>
      ) : (
        <div className="not-disable">
          <div className="table-responsive">
            <table className="table subscription-table1 table-sm w-100">
              <thead>
                <tr>
                  <SortableHeader
                    column="subscription_name"
                    label="Subscription Name"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />
                  <th>Description</th>
                  <SortableHeader
                    column="subscription_amount"
                    label="Amount"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="subscription_plan_type"
                    label="Plan Type"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="subscription_start_date"
                    label="Start Date"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="subscription_end_date"
                    label="End Date"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="subscription_status"
                    label="Status"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <SortableHeader
                    column="updated_timestamp"
                    label="Last Updated"
                    sortConfig={sortConfig}
                    onSort={requestSort}
                  />

                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedData.map((s) => (
                  <tr key={s.subscription_id}>
                    <td
                      title={s.subscription_name}
                      style={{
                        maxWidth: "190px",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {s.subscription_name
                        ? s.subscription_name.charAt(0).toUpperCase() +
                          s.subscription_name.slice(1)
                        : "—"}
                    </td>
                    <td title={s.subscription_description}>
                      {s.subscription_description || "—"}
                    </td>
                    <td>{s.subscription_amount}</td>
                    <td>{s.subscription_plan_type}</td>
                    <td>{formatDateToUser(s.subscription_start_date)}</td>
                    <td>{formatDateToUser(s.subscription_end_date)}</td>
                    <td>
                      {s.subscription_status === "1"
                        ? "Active"
                        : s.subscription_status === "0"
                          ? "Inactive"
                          : "Expired"}
                    </td>
                    <td>{formatDateToUserTimeZone(s.updated_timestamp)}</td>
                    <td>
                      <button
                        className="icon-button"
                        onClick={() => handleView(s)}
                        title="View"
                      >
                        <img src={viewIcon} className="icon-image" alt="View" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleEdit(s)}
                        title="Edit"
                      >
                        <img src={editIcon} className="icon-image" alt="Edit" />
                      </button>
                      <button
                        className="icon-button"
                        onClick={() => handleChangeStatus(s)}
                        title={
                          s.subscription_status === "2"
                            ? "Expired subscriptions cannot change status"
                            : "Change Status"
                        }
                        disabled={s.subscription_status === "2"}
                      >
                        <img
                          src={changeStatusIcon}
                          className="icon-image"
                          alt="Change Status"
                        />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* <div className="d-flex justify-content-between align-items-center mt-2 table-footer"> */}
          <div className="table-footer fixed-bottom-footer d-flex justify-content-between align-items-center">
            <div className="refresh-prompts">
              Showing {showingFrom} to {showingTo} of {totalEntries} entries
            </div>
            <nav>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(1)}
                  >
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {pageNumbers.map((num) => (
                  <li
                    key={num}
                    className={`page-item ${
                      num === currentPage ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageClick(num)}
                    >
                      {num}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageClick(totalPages)}
                  >
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      )}

      {showForm && (
        <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content w-75 mx-auto">
              <form onSubmit={handleFormSubmit}>
                {/* Modal Header */}
                <div className="modal-header  justify-content-center position-relative">
                  <h5 className="modal-title m-0">
                    {viewMode
                      ? "View Subscription"
                      : editingSubscriptionId
                        ? "Update Subscription"
                        : "Add Subscription"}
                  </h5>
                  <button
                    type="button"
                    className="modal-close-btn"
                    onClick={closeModal}
                    aria-label="Close"
                  >
                    <img src={crossIcon} className="icon-image" alt="Close" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label required-label">
                        Subscription Name
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.subscription_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subscription_name: e.target.value,
                          })
                        }
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please provide a Subscription Name.",
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                        required
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({ ...formData, amount: e.target.value })
                        }
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label required-label">
                        Plan Type
                      </label>
                      <select
                        className="form-select"
                        value={formData.plan_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            plan_type: e.target.value,
                          })
                        }
                        onInvalid={(e) =>
                          e.target.setCustomValidity(
                            "Please select a  plan type.",
                          )
                        }
                        onInput={(e) => e.target.setCustomValidity("")}
                        required
                        disabled={viewMode}
                      >
                        <option value="Select Plan Type">
                          Select Plan Type
                        </option>
                        <option value="Basic">Basic</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label required-label">
                      Description
                    </label>
                    <textarea
                      className="input-textarea1"
                      rows="2"
                      value={formData.subscription_description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subscription_description: e.target.value,
                        })
                      }
                      onInvalid={(e) =>
                        e.target.setCustomValidity(
                          "Please provide a descritption.",
                        )
                      }
                      onInput={(e) => e.target.setCustomValidity("")}
                      required
                      disabled={viewMode}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <label className="form-label required-label">
                        Start Date
                      </label>
                      <DatePicker
                        className="datecolor"
                        calendarClassName="datecolor"
                        required
                        selected={
                          formData.start_date
                            ? new Date(formData.start_date)
                            : null
                        }
                        onChange={(date) => {
                          const iso = date?.toISOString().split("T")[0];
                          setFormData((prev) => ({
                            ...prev,
                            start_date: iso,
                            end_date: prev.end_date < iso ? "" : prev.end_date,
                          }));
                        }}
                        dateFormat="MM/dd/yyyy"
                        minDate={new Date()}
                        customInput={<CustomDateInput />}
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label required-label">
                        End Date
                      </label>
                      <DatePicker
                        selected={
                          formData.end_date ? new Date(formData.end_date) : null
                        }
                        required
                        onChange={(date) => {
                          const iso = date?.toISOString().split("T")[0];
                          setFormData((prev) => ({ ...prev, end_date: iso }));
                        }}
                        dateFormat="MM/dd/yyyy"
                        minDate={
                          formData.start_date
                            ? new Date(formData.start_date)
                            : new Date()
                        }
                        customInput={<CustomDateInput />}
                        disabled={viewMode}
                      />
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.subscription_status || "1"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subscription_status: e.target.value,
                          })
                        }
                        required
                        disabled={viewMode}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                        <option value="2">Expired</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                  {!viewMode && (
                    <button
                      type="submit"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? editingSubscriptionId
                          ? "Updating..."
                          : "Adding..."
                        : editingSubscriptionId
                          ? "Update Subscription"
                          : "Add Subscription"}
                    </button>
                  )}
                  {viewMode && (
                    <button
                      type="button"
                      className="cancle-btn"
                      onClick={closeModal}
                    >
                      Close
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
